import { Injectable, inject } from '@angular/core';
import { trace } from '@opentelemetry/api';
import { contexteUtilisateur } from '@services/core/telemetrie';
import { PhotoBlobService } from '@services/core/photo-blob.service';
import { VignetteService } from '@services/core/vignette.service';
import { PerformanceMonitorService } from '@services/core/performance-monitor.service';

/** Ancien emplacement, PARTAGÉ par tous les onglets (avant la 2.1.48) : effacé au démarrage. */
const CLE_ANCIENNE = 'atlas.sentinelle';
/** Un état par onglet : `atlas.sentinelle.<onglet>`. */
const PREFIXE = 'atlas.sentinelle.';
/** Identifiant de l'onglet (sessionStorage : survit au rechargement, propre à l'onglet). */
const CLE_ONGLET = 'atlas.sentinelle-onglet';
const BATTEMENT_MS = 10_000;
/** Délai avant de conclure qu'un autre onglet est arrêté : il aurait battu entre-temps. */
const VERIFICATION_MS = 15_000;
/** Au-delà, l'arrêt est trop ancien pour être relié à la session (appareil éteint…). */
const ANCIENNETE_MAX_MS = 6 * 3600 * 1000;

interface EtatSession {
	debut: number;
	dernier: number;
	/** L'appli était à l'écran au dernier battement (un arrêt en arrière-plan n'est pas un crash). */
	visible: boolean;
	/** Fermeture normale (pagehide) : rechargement, mise à jour, navigation. */
	propre: boolean;
	route: string;
	biId: number | null;
	photos: number;
	vignettes: number;
	elements: number;
	images: number;
	memoireMo: number | null;
	/** Fenêtre ouverte et dernière interaction au dernier battement (ex. outil d'annotation). */
	action?: string;
}

/**
 * Détection des ARRÊTS BRUTAUX de l'appli (« page blanche » intermittente sur iPad).
 *
 * Quand Safari manque de mémoire, il tue l'onglet et le recharge : aucune erreur ne
 * remonte, l'appli est tuée net. On note donc régulièrement sur l'appareil où en est la
 * session ; au démarrage suivant, si la précédente s'est arrêtée À L'ÉCRAN sans
 * fermeture normale, on envoie `app.arret_brutal` avec son dernier état (écran, dossier,
 * photos et miniatures en mémoire, éléments affichés). Sert à mesurer la baisse des
 * crashs après chaque correctif mémoire (02/10).
 */
@Injectable({ providedIn: 'root' })
export class SentinelleService {
	private photoBlobService = inject(PhotoBlobService);
	private vignettes = inject(VignetteService);
	private perf = inject(PerformanceMonitorService);
	private etat: EtatSession | null = null;
	private demarree = false;
	private cle = CLE_ANCIENNE;

	/**
	 * L'état était partagé par tous les onglets : ouvrir un nouvel onglet (historique,
	 * dossier) pendant qu'un autre restait ouvert lisait l'état de ce dernier, « non fermé »,
	 * et signalait un arrêt brutal qui n'avait pas eu lieu — la majorité des arrêts sur
	 * ordinateur côté BE / BA (14/10). Chaque onglet a maintenant son état, et un arrêt
	 * n'est signalé qu'une fois vérifié que l'onglet ne bat plus.
	 */
	demarrer(): void {
		if (this.demarree || typeof window === 'undefined') return;
		this.demarree = true;
		const onglet = this.idOnglet();
		this.cle = PREFIXE + onglet;
		const interrompus = this.etatsInterrompus();
		const maintenant = Date.now();
		this.etat = {
			debut: maintenant, dernier: maintenant, visible: document.visibilityState === 'visible',
			propre: false, route: '', biId: null, photos: 0, vignettes: 0, elements: 0, images: 0, memoireMo: null,
		};
		this.battre();
		setInterval(() => this.battre(), BATTEMENT_MS);
		document.addEventListener('visibilitychange', () => this.battre());
		window.addEventListener('pagehide', () => {
			if (!this.etat) return;
			this.etat.propre = true;
			this.ecrire();
		});

		if (interrompus.length) {
			// Laisser à l'utilisateur et à l'export de télémétrie le temps d'être prêts, et
			// aux autres onglets celui de battre s'ils sont toujours ouverts.
			setTimeout(() => {
				for (const { cle, etat } of interrompus) {
					// Ce même onglet, rechargé sans fermeture normale : arrêté, sans aucun doute
					// (et son état a déjà été remplacé par la nouvelle session).
					if (cle === this.cle) { this.signalerArret(etat); continue; }
					const actuel = this.lireCle(cle);
					// Il a battu depuis (onglet ouvert) ou s'est fermé normalement : pas d'arrêt.
					if (!actuel || actuel.propre || actuel.dernier !== etat.dernier) continue;
					this.signalerArret(actuel);
					try { localStorage.removeItem(cle); } catch { /* sans importance */ }
				}
			}, VERIFICATION_MS);
		}
	}

	private idOnglet(): string {
		try {
			let id = sessionStorage.getItem(CLE_ONGLET);
			if (!id) {
				id = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
				sessionStorage.setItem(CLE_ONGLET, id);
			}
			return id;
		} catch {
			return 'unique';
		}
	}

	/**
	 * États des onglets arrêtés à l'écran sans fermeture normale (candidats à vérifier) ;
	 * au passage, efface les états périmés ou fermés proprement, et l'ancien état partagé.
	 */
	private etatsInterrompus(): { cle: string; etat: EtatSession }[] {
		const res: { cle: string; etat: EtatSession }[] = [];
		try {
			localStorage.removeItem(CLE_ANCIENNE);
			const cles: string[] = [];
			for (let i = 0; i < localStorage.length; i++) {
				const cle = localStorage.key(i);
				if (cle && cle.startsWith(PREFIXE)) cles.push(cle);
			}
			for (const cle of cles) {
				const etat = this.lireCle(cle);
				const recent = !!etat && Date.now() - etat.dernier < ANCIENNETE_MAX_MS;
				if (etat && recent && !etat.propre && etat.visible) res.push({ cle, etat });
				else if (!etat || !recent || etat.propre) localStorage.removeItem(cle);
			}
		} catch {
			// Stockage indisponible : on ne mesure pas.
		}
		return res;
	}

	private battre(): void {
		if (!this.etat) return;
		try {
			const route = location.pathname || '';
			const bi = /\/rapport\/(\d+)/.exec(route);
			const memoire = (performance as any)?.memory?.usedJSHeapSize;
			Object.assign(this.etat, {
				dernier: Date.now(),
				visible: document.visibilityState === 'visible',
				route,
				biId: bi ? Number(bi[1]) : null,
				photos: this.photoBlobService.tailleCache,
				vignettes: this.vignettes.taille,
				elements: document.getElementsByTagName('*').length,
				images: document.images.length,
				memoireMo: typeof memoire === 'number' ? Math.round(memoire / 1048576) : null,
				action: this.perf.actionEnCours(),
			});
			this.ecrire();
		} catch {
			// Mesure seulement : ne jamais gêner l'appli.
		}
	}

	private signalerArret(etat: EtatSession): void {
		try {
			const span = trace.getTracer('atlas-v2-web').startSpan('app.arret_brutal');
			const utilisateur = contexteUtilisateur();
			if (utilisateur.username) span.setAttribute('user.name', utilisateur.username);
			if (utilisateur.email) span.setAttribute('user.email', utilisateur.email);
			span.setAttribute('arret.route', etat.route);
			if (etat.biId != null) span.setAttribute('bi.id', etat.biId);
			span.setAttribute('arret.session_min', Math.round((etat.dernier - etat.debut) / 60000));
			span.setAttribute('arret.age_s', Math.round((Date.now() - etat.dernier) / 1000));
			span.setAttribute('arret.photos', etat.photos);
			span.setAttribute('arret.vignettes', etat.vignettes);
			span.setAttribute('arret.elements', etat.elements);
			span.setAttribute('arret.images', etat.images);
			if (etat.memoireMo != null) span.setAttribute('arret.memoire_mo', etat.memoireMo);
			if (etat.action) span.setAttribute('arret.action', etat.action);
			span.end();
		} catch {
			// Mesure seulement.
		}
	}

	private lireCle(cle: string): EtatSession | null {
		try {
			const brut = localStorage.getItem(cle);
			return brut ? JSON.parse(brut) as EtatSession : null;
		} catch {
			return null;
		}
	}

	private ecrire(): void {
		try {
			if (this.etat) localStorage.setItem(this.cle, JSON.stringify(this.etat));
		} catch {
			// Stockage indisponible (navigation privée…) : tant pis, on ne mesure pas.
		}
	}
}
